import { ExtendedColumnDef } from "components/Datatable";
import { format, differenceInHours, addHours, isAfter } from "date-fns";
import { ptBR } from "date-fns/locale";
import { EmailData } from "./types";
import { ReviewStatus } from "./types/shared";
import { Button } from "components/ui/button";
import { ClipboardCheck } from "lucide-react";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { EmailDetailsModal } from "./components/modals";
import { formatOperatorName } from "@/utils/format-operator-name";

interface IUseColumnsProps {
  onShowReview?: (email: EmailData) => void;
}

export const useColumns = ({
  onShowReview
}: IUseColumnsProps): ExtendedColumnDef<EmailData>[] => {
  const { handleModal } = useDialogModal();


  return [
    {
      accessorKey: "requested_by_email",
      header: "Analista",
      className: "min-w-[140px]",
      cell: ({ row }) => {
        const email = row.getValue("requested_by_email") as string;
        const operator = formatOperatorName(email);
        return (
          <div className="text-sm">
            {operator}
          </div>
        );
      },
    },
    {
      accessorKey: "totalItens",
      header: "Total de Itens",
      className: "min-w-[120px]",
      cell: ({ row }) => {
        const email = row.original;
        const pendingDivergences = email.divergences.filter(d => d.review_status === ReviewStatus.PENDING_ANSWER).length;
        const waitingDivergences = email.divergences.filter(d => d.review_status === ReviewStatus.REJECTED_WAITING_FOR_RESPOND).length;
        const totalDivergences = email.totalDivergences;

        const pendingNsus = email.nsus.filter(n => n.review_status === ReviewStatus.PENDING_ANSWER).length;
        const waitingNsus = email.nsus.filter(n => n.review_status === ReviewStatus.REJECTED_WAITING_FOR_RESPOND).length;

        const totalNsus = email.totalNsus;

        const totalItens = totalDivergences + totalNsus;
        const pendingItens = pendingDivergences + pendingNsus;
        const waitingItens = waitingDivergences + waitingNsus;

        return (
            <div className="text-sm">
              <span className="font-medium">{totalItens}</span>
              {pendingItens > 0 && (
                <span className="text-blue-600 text-xs ml-1">
                  ({pendingItens} pendente)
                </span>
              )}
              {waitingItens ? (
                <span className="text-red-600 text-xs ml-1">
                  ({waitingItens} aguardando)
                </span>
              ) : null}
            </div>
        );
      },
    },
    {
      accessorKey: "sent_at",
      header: "Enviado em",
      className: "min-w-[140px]",
      cell: ({ row }) => {
        const date = row.getValue("sent_at") as string;
        return (
          <div className="text-sm">
            {format(new Date(date), "dd/MM HH:mm", { locale: ptBR })}
          </div>
        );
      },
    },
    {
      id: "deadline",
      header: "Prazo de Resposta",
      className: "min-w-[160px]",
      cell: ({ row }) => {
        const email = row.original;
        const sentAt = new Date(email.sent_at);
        const now = new Date();
        const deadline = addHours(sentAt, 24); 

        const hasRejectedWaitingItems = email.divergences.some(d => d.review_status === ReviewStatus.REJECTED_WAITING_FOR_RESPOND) ||
                                       email.nsus.some(n => n.review_status === ReviewStatus.REJECTED_WAITING_FOR_RESPOND);

        if (hasRejectedWaitingItems) {
          return (
            <div className="flex items-center gap-2 text-sm">
              <div className="w-2 h-2 bg-red-500 rounded-full" />
              <span className="text-red-600 font-medium">
                Aguardando Resposta
              </span>
            </div>
          );
        }

        if (email.is_finalized) {
          return (
            <div className="flex items-center gap-2 text-sm">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <span className="text-green-600 font-medium">
                Respondido
              </span>
            </div>
          );
        }

        if (isAfter(now, deadline)) {
          const hoursOverdue = differenceInHours(now, deadline);
          return (
            <div className="flex items-center gap-2 text-sm">
              <div className="w-2 h-2 bg-red-500 rounded-full" />
              <span className="text-red-600 font-medium">
                Expirado ({hoursOverdue}h atrasado)
              </span>
            </div>
          );
        }

        const hoursLeft = differenceInHours(deadline, now);
        const colorClass = hoursLeft <= 2 ? "text-orange-600" : "text-blue-600";
        const bgColor = hoursLeft <= 2 ? "bg-orange-500" : "bg-blue-500";

        return (
          <div className="flex items-center gap-2 text-sm">
            <div className={`w-2 h-2 ${bgColor} rounded-full`} />
            <span className={`${colorClass} font-medium`}>
              {hoursLeft}h restante(s)
            </span>
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Ações",
      cell: ({ row }) => {
        const email = row.original;
        const handleViewDetails = () => {
          if (onShowReview) {
            onShowReview(email);
          } else {
            const modalData = EmailDetailsModal({ email });
            handleModal({
              isOpen: true,
              element: modalData.element,
              title: modalData.title
            });
          }
        };

        return (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-3 flex items-center gap-1.5"
              onClick={handleViewDetails}
            >
              <ClipboardCheck className="h-3 w-3" />
              <span className="text-xs font-medium">Revisar Evidências</span>
            </Button>
          </div>
        );
      },
    },
  ];
};
